/* global mainScript */
import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { useMediaQuery } from "react-responsive";

// State
import Context from "./Context";

// comps
import SidePanelSponsors from "./SidePanelSponsors";

//style rules
import { Sizes, Animation, Colors } from "../../styles";

const SidePanel = ({ selectedMediaItem }) => {
	const {
		actions: { toggleSidePanel },
	} = useContext(Context);

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.large})`,
	});

	const renderTags = () => {
		let tagsArr = [];
		// we need to combine both decade and themes and then loop over them
		if (selectedMediaItem.themes) {
			selectedMediaItem.themes.map((theme) => {
				return tagsArr.push(theme);
			});
		}

		if (selectedMediaItem.decade) {
			tagsArr = [...tagsArr, selectedMediaItem.decade];
		}

		return (
			tagsArr &&
			tagsArr.length > 0 &&
			tagsArr.map((tag) => {
				return (
					<Tag key={tag.slug} dangerouslySetInnerHTML={{ __html: tag.title }} />
				);
			})
		);
	};

	const renderButton = () => {
		return (
			selectedMediaItem.link && (
				<a
					className="button"
					href={selectedMediaItem.link.url}
					target={selectedMediaItem.link.target}
				>
					{selectedMediaItem.link.title}
				</a>
			)
		);
	};

	return (
		<>
			<Overlay
				initial={"closed"}
				animate={"opened"}
				exit={"closed"}
				variants={Animation.sidepanel.overlayVariants}
				onClick={() => toggleSidePanel()}
			/>
			<Aside
				initial={{ width: 0, opacity: 0 }}
				animate={{ width: isDesktopOrLaptop ? 1188.14 : "100vw", opacity: 1 }}
				exit={{
					width: 0,
					opacity: 0,
					transition: { delay: 0.5 },
				}}
			>
				<CloseIcon
					initial={"closed"}
					animate={"open"}
					exit={"exit"}
					variants={Animation.sidepanel.opacityVariants}
					icon={mainScript.assetPath + "images/svg/icons/icon-close.svg"}
					onClick={() => toggleSidePanel()}
				/>
				<Wrapper
					initial={"closed"}
					animate={"open"}
					exit={"closed"}
					variants={Animation.sidepanel.opacityVariants}
				>
					<ImageContainer className="imageContainer">
						{selectedMediaItem.image ? (
							<img
								src={selectedMediaItem.image}
								alt={`image for ${selectedMediaItem.title}`}
							/>
						) : (
							<img
								src={
									mainScript.assetPath +
									"images/png/graphics/story-placeholder.png"
								}
								alt={"Side panel placeholder"}
							/>
						)}
					</ImageContainer>
					<Title
						dangerouslySetInnerHTML={{ __html: selectedMediaItem.title }}
					/>
					{selectedMediaItem.content && (
						<Text
							dangerouslySetInnerHTML={{ __html: selectedMediaItem.content }}
						/>
					)}
					<Tags>{renderTags()}</Tags>
					<ButtonContainer>{renderButton()}</ButtonContainer>
					{selectedMediaItem.sponsors &&
						selectedMediaItem.sponsors.length > 0 && (
						<SidePanelSponsors sponsors={selectedMediaItem.sponsors} />
					)}
				</Wrapper>
			</Aside>
		</>
	);
};

export default SidePanel;

SidePanel.propTypes = {
	selectedMediaItem: PropTypes.object,
};

const Aside = styled(motion.aside)`
	overflow: scroll;
	background-color: ${Colors.base.beige};
	height: 100vh;
	position: fixed;
	right: 0;
	top: 0;
	z-index: 1000;
`;

const CloseIcon = styled(motion.svg)`
	position: absolute;
	right: 5rem;
	top: 5rem;
	content: "";
	background: url(${(props) => props.icon && props.icon}) no-repeat;
	width: 29px;
	height: 29px;
	margin: 0.4rem 0 0 0.5rem;
	cursor: pointer;
`;

const Wrapper = styled(motion.div)`
	padding: 7.3rem 7.9rem;
`;

const ImageContainer = styled.div`
	max-width: 759px;

	img {
		width: 100%;
		height: auto;
	}
`;

const Title = styled.h4`
	font-size: 4.8rem;
	line-height: 5.4rem;
	letter-spacing: -0.0095em;
	border-top: 4px solid ${Colors.base.black};
	border-bottom: 4px solid ${Colors.base.black};
	padding: 1.9rem 0;
	margin-top: 6.2rem;
`;

const Text = styled.div`
	padding-top: 3.4rem;

	p:not(:first-of-type) {
		padding-top: 2.4rem;
	}

	a {
		letter-spacing: normal;
	}
`;

const Tags = styled.div`
	padding-top: 3rem;
	display: flex;
	gap: 2rem;
	flex-wrap: wrap;
`;
const Tag = styled.span`
	font-weight: 700;
	font-size: 1.6rem;
	line-height: 2.6rem;
	letter-spacing: -0.004em;
	text-transform: uppercase;
	padding: 0.3rem 0.9rem;
	background: rgba(31, 31, 31, 0.9);
	color: ${Colors.base.white};
`;

const ButtonContainer = styled.div`
	padding-top: 5rem;
`;

const Overlay = styled(motion.div)`
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	cursor: pointer;
	width: 100vw;
	height: 100vh;
	z-index: 900;
	background: ${Colors.base.black};
	opacity: 0.5;
`;
